import SaveIcon from '@mui/icons-material/Save';
import { CircularProgress } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImageBox } from '../../components/BoxedImage';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import {
  configItemsToRecord,
  usePlanogramComplianceConfig,
} from '../../hooks/usePlanogramComplianceConfig';
import { useVariantSelection } from '../../hooks/useVariantSelection';
import { ApiService, composeProductRecognitionFieldsWithShapes } from '../../services/api';
import { GtApiService, GtSampleResponse } from '../../services/gtApi';
import { PlanogramComplianceResponse, ProductRecognitionResponse } from '../../types';
import { addRogPosition, moveRogPosition, removeRogPosition } from '../../utils/rogPositionShift';
import {
  DetectionPogPairing,
  GtAudit,
  PcInferenceCamel,
  PogResultStatus,
} from './components/GtAudit';
import { RogItemStatusValue } from './components/rogStatusUtils';
import { VariantSelector } from './components/VariantSelector';

interface GroundTruthAuditProps {
  gtSampleId: string | null;
}

export const GroundTruthAudit: React.FC<GroundTruthAuditProps> = ({ gtSampleId }) => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();

  const [productRecognitionData, setProductRecognitionData] =
    useState<ProductRecognitionResponse | null>(null);
  const [planogramComplianceData, setPlanogramComplianceData] =
    useState<PlanogramComplianceResponse | null>(null);
  const [pcInference, setPcInference] = useState<PcInferenceCamel | null>(null);
  const [isPcDirty, setIsPcDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingPr, setIsSavingPr] = useState(false);
  const [isSavingPc, setIsSavingPc] = useState(false);
  const [imageId, setImageId] = useState<string | null>(null);
  const { config: planogramComplianceConfig } = usePlanogramComplianceConfig(
    imageId ? Number(imageId) : null
  );

  const [storeName, setStoreName] = useState<string | null>(null);
  const [currentPairings, setCurrentPairings] = useState<Map<string, DetectionPogPairing>>(
    new Map()
  );

  const {
    loadingUpcs,
    fetchVariantsForUpc,
    selectVariant,
    clearVariant,
    getSelectedVariant,
    getVariantsForUpc,
    allVariantsSelected,
    selectedVariants,
  } = useVariantSelection();

  // Track previous pairing keys to detect deletions
  const prevPairingKeysRef = useRef<Set<string>>(new Set());

  const fetchGtSample = useCallback(async () => {
    if (!gtSampleId || !token) return;
    setIsLoading(true);
    try {
      const gtSample: GtSampleResponse = await GtApiService.getGtSample(token, gtSampleId);
      setImageId(gtSample.imageId != null ? String(gtSample.imageId) : null);
      const prData = gtSample.prInference as ProductRecognitionResponse | undefined;
      if (prData) {
        const fieldsWithShapes = composeProductRecognitionFieldsWithShapes(prData);
        setProductRecognitionData({ ...prData, ...fieldsWithShapes });
      }
      setPcInference((gtSample.pcInference ?? null) as PcInferenceCamel | null);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to fetch GT sample');
    } finally {
      setIsLoading(false);
    }
  }, [gtSampleId, token, showErrorToast]);

  useEffect(() => {
    fetchGtSample();
  }, [fetchGtSample]);

  useEffect(() => {
    const fetchPlanogramCompliance = async () => {
      if (
        !token ||
        !productRecognitionData ||
        planogramComplianceData ||
        !planogramComplianceConfig
      ) {
        return;
      }
      try {
        const payload = {
          ...productRecognitionData,
          config: configItemsToRecord(planogramComplianceConfig),
        };
        const data = await ApiService.createPlanogramComplianceFromPayload(token, payload, true);
        setPlanogramComplianceData(data);
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Failed to fetch planogram compliance');
      }
    };
    fetchPlanogramCompliance();
  }, [
    token,
    productRecognitionData,
    planogramComplianceData,
    planogramComplianceConfig,
    showErrorToast,
  ]);

  // Fetch variants when pairings change
  useEffect(() => {
    if (!token || !storeName) return;

    // Fetch variants for any new UPCs (skip null-UPC pairings)
    for (const [, pairing] of currentPairings) {
      if (pairing.upc) fetchVariantsForUpc(token, storeName, pairing.upc);
    }

    // Clean up selections for deleted pairings
    const currentKeys = new Set(currentPairings.keys());
    for (const prevKey of prevPairingKeysRef.current) {
      if (!currentKeys.has(prevKey)) {
        clearVariant(prevKey);
      }
    }
    prevPairingKeysRef.current = currentKeys;
  }, [currentPairings, token, storeName, fetchVariantsForUpc, clearVariant]);

  const handleDetectionPogPairingsChange = useCallback(
    (pairings: Map<string, DetectionPogPairing>) => {
      setCurrentPairings(pairings);
    },
    []
  );

  const handleStoreNameChange = useCallback((name: string) => {
    setStoreName(name);
  }, []);

  const handleVariantSelect = useCallback(
    (detectionKey: string, variant: import('../../types').ProductVariantResponse) => {
      selectVariant(detectionKey, variant);
    },
    [selectVariant]
  );

  const pairingAddon = useCallback(
    (pairing: DetectionPogPairing & { detectionKey: string }) => {
      if (!pairing.upc) return null;

      const variants = getVariantsForUpc(pairing.upc) || [];
      const selected = getSelectedVariant(pairing.detectionKey);
      const isLoadingVariants = loadingUpcs.has(pairing.upc);

      return (
        <VariantSelector
          detectionKey={pairing.detectionKey}
          variants={variants}
          selectedVariant={selected}
          isLoading={isLoadingVariants}
          onSelectVariant={handleVariantSelect}
        />
      );
    },
    [getVariantsForUpc, getSelectedVariant, loadingUpcs, handleVariantSelect]
  );

  const applyVariantsToPrData = useCallback(
    (prData: ProductRecognitionResponse): ProductRecognitionResponse => {
      if (selectedVariants.size === 0) return prData;

      const modified: ProductRecognitionResponse = JSON.parse(JSON.stringify(prData));

      // Build a map from shape key to selected variant
      const shapeToVariant = new Map<string, import('../../types').ProductVariantResponse>();
      for (const [detectionKey, variant] of selectedVariants) {
        const pairing = currentPairings.get(detectionKey);
        if (pairing?.detection.shape) {
          shapeToVariant.set(pairing.detection.shape.join(','), variant);
        }
      }

      Object.keys(modified).forEach((layerKey) => {
        const layer = modified[layerKey as keyof ProductRecognitionResponse];
        if (Array.isArray(layer)) {
          layer.forEach((detection: any) => {
            if (detection.shape) {
              const shapeKey = Array.isArray(detection.shape) ? detection.shape.join(',') : '';
              const variant = shapeToVariant.get(shapeKey);
              if (variant && detection.vizSimCandidates?.length > 0) {
                detection.vizSimCandidates[0].normalizedBarcode = variant.upc;
                if (variant.name) {
                  detection.vizSimCandidates[0].productName = variant.name;
                }
                if (variant.frontImage) {
                  detection.vizSimCandidates[0].productImageUri = variant.frontImage;
                }
              }
            }
          });
        }
      });

      return modified;
    },
    [selectedVariants, currentPairings]
  );

  const renderTitleActions = useCallback(
    ({
      selectedDetections,
      setDetectionPogPairings,
      setSelectedDetections,
    }: {
      selectedDetections: ImageBox[];
      setDetectionPogPairings: React.Dispatch<
        React.SetStateAction<Map<string, DetectionPogPairing>>
      >;
      setSelectedDetections: React.Dispatch<React.SetStateAction<ImageBox[]>>;
    }) => {
      const handleSpecialPairing = (
        type: 'unidentifiable' | 'no_data',
        confidence: number,
        isConfident: boolean
      ) => {
        if (selectedDetections.length === 0) return;

        setDetectionPogPairings((prev) => {
          const newMap = new Map(prev);
          selectedDetections.forEach((detection) => {
            if (!detection.shape) return;
            const detectionKey = detection.shape.join(',');
            newMap.set(detectionKey, {
              detection,
              upc: null,
              name: type === 'unidentifiable' ? 'Unidentifiable' : 'No data',
              productId: null,
              source: type,
              confidence,
              isConfident,
              timestamp: new Date(),
            });
          });
          return newMap;
        });
        setSelectedDetections([]);
      };

      return (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSpecialPairing('unidentifiable', 0.5, false)}
            disabled={selectedDetections.length === 0}
            className="px-3 py-1 rounded text-sm font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Unidentifiable
          </button>
          <button
            onClick={() => handleSpecialPairing('no_data', -1, true)}
            disabled={selectedDetections.length === 0}
            className="px-3 py-1 rounded text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            No data
          </button>
        </div>
      );
    },
    []
  );

  const applyPairingsToPrData = useCallback(
    (prData: ProductRecognitionResponse): ProductRecognitionResponse => {
      if (currentPairings.size === 0) return prData;

      const modified: ProductRecognitionResponse = JSON.parse(JSON.stringify(prData));

      Object.keys(modified).forEach((layerKey) => {
        const layer = modified[layerKey as keyof ProductRecognitionResponse];
        if (Array.isArray(layer)) {
          layer.forEach((detection: any) => {
            if (detection.shape) {
              const shapeKey = Array.isArray(detection.shape) ? detection.shape.join(',') : '';
              const pairing = currentPairings.get(shapeKey);

              if (pairing) {
                const pairingConfidence = pairing.confidence ?? 1;
                const pairingIsConfident = pairing.isConfident ?? true;

                detection.confidence = pairingConfidence;

                if (!detection.vizSimCandidates) {
                  detection.vizSimCandidates = [];
                }

                if (pairing.upc === null) {
                  if (detection.vizSimCandidates.length > 0) {
                    detection.vizSimCandidates[0].isConfident = pairingIsConfident;
                    detection.vizSimCandidates[0].normalizedBarcode = null;
                    detection.vizSimCandidates[0].similarity = pairingIsConfident ? 1 : 0;
                  } else {
                    detection.vizSimCandidates.unshift({
                      normalizedBarcode: null,
                      similarity: pairingIsConfident ? 1 : 0,
                      isConfident: pairingIsConfident,
                      productName: pairing.name || '',
                      itemNumber: '',
                      productImageUri: '',
                      productId: undefined,
                    });
                  }
                } else {
                  const existingCandidateIndex = detection.vizSimCandidates.findIndex(
                    (candidate: any) => candidate.normalizedBarcode === pairing.upc
                  );

                  if (existingCandidateIndex !== -1) {
                    detection.vizSimCandidates[existingCandidateIndex].isConfident =
                      pairingIsConfident;
                    detection.vizSimCandidates[existingCandidateIndex].similarity = 1;
                    detection.vizSimCandidates.sort(
                      (a: any, b: any) => (b.similarity || 0) - (a.similarity || 0)
                    );
                  } else {
                    detection.vizSimCandidates.unshift({
                      normalizedBarcode: pairing.upc,
                      similarity: 1,
                      isConfident: pairingIsConfident,
                      productName: pairing.name || '',
                      itemNumber: '',
                      productImageUri: pairing.imageUri || pairing.pogBox?.imageUri || '',
                      productId: undefined,
                    });
                  }
                }
              }
            }
          });
        }
      });

      return modified;
    },
    [currentPairings]
  );

  const handlePogStatusApply = useCallback((ilItemId: number, status: PogResultStatus) => {
    setPcInference((previous) => ({
      ...(previous ?? { pogResultsByIli: {}, rogResultsByPosition: {} }),
      pogResultsByIli: {
        ...(previous?.pogResultsByIli ?? {}),
        [String(ilItemId)]: status,
      },
      rogResultsByPosition: previous?.rogResultsByPosition ?? {},
    }));
    setIsPcDirty(true);
  }, []);

  const handleRogStatusApply = useCallback(
    (positionKey: string, statuses: RogItemStatusValue[]) => {
      setPcInference((previous) => {
        const existing = previous?.rogResultsByPosition?.[positionKey];
        if (!existing) return previous;
        return {
          ...(previous ?? { pogResultsByIli: {}, rogResultsByPosition: {} }),
          pogResultsByIli: previous?.pogResultsByIli ?? {},
          rogResultsByPosition: {
            ...(previous?.rogResultsByPosition ?? {}),
            [positionKey]: { ...existing, rogItemStatus: statuses },
          },
        };
      });
      setIsPcDirty(true);
    },
    []
  );

  const handleRemoveRogPosition = useCallback((mod: number, shelf: number, column: number) => {
    setPcInference((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        rogResultsByPosition: removeRogPosition(
          previous.rogResultsByPosition ?? {},
          mod,
          shelf,
          column
        ),
      };
    });
    setIsPcDirty(true);
  }, []);

  const handleAddRogPosition = useCallback(
    (mod: number, shelf: number, column: number, status: RogItemStatusValue) => {
      setPcInference((previous) => {
        const base = previous ?? { pogResultsByIli: {}, rogResultsByPosition: {} };
        return {
          ...base,
          rogResultsByPosition: addRogPosition(
            base.rogResultsByPosition ?? {},
            mod,
            shelf,
            column,
            { finalItemNumber: null, rogItemStatus: [status], listOfPrUuids: [] }
          ),
        };
      });
      setIsPcDirty(true);
    },
    []
  );

  const handleTogglePrUuidForRogPosition = useCallback((positionKey: string, uuid: string) => {
    if (!uuid) return;
    setPcInference((previous) => {
      if (!previous) return previous;
      const existing = previous.rogResultsByPosition?.[positionKey];
      if (!existing) return previous;
      const current = existing.listOfPrUuids ?? [];
      const next = current.includes(uuid) ? current.filter((u) => u !== uuid) : [...current, uuid];
      return {
        ...previous,
        rogResultsByPosition: {
          ...previous.rogResultsByPosition,
          [positionKey]: { ...existing, listOfPrUuids: next },
        },
      };
    });
    setIsPcDirty(true);
  }, []);

  const handleMoveRogPosition = useCallback(
    (
      fromMod: number,
      fromShelf: number,
      fromColumn: number,
      toMod: number,
      toShelf: number,
      toColumn: number
    ) => {
      setPcInference((previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          rogResultsByPosition: moveRogPosition(
            previous.rogResultsByPosition ?? {},
            fromMod,
            fromShelf,
            fromColumn,
            toMod,
            toShelf,
            toColumn
          ),
        };
      });
      setIsPcDirty(true);
    },
    []
  );

  const handleSavePr = useCallback(async () => {
    if (!gtSampleId || !token || !productRecognitionData) return;
    setIsSavingPr(true);
    try {
      const withPairings = applyPairingsToPrData(productRecognitionData);
      const finalPrData = applyVariantsToPrData(withPairings);
      const payload = { pr_inference: finalPrData };
      await GtApiService.updateGtSamplePr(token, gtSampleId, payload);
      showSuccessToast('PR inference saved');
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to save PR inference');
    } finally {
      setIsSavingPr(false);
    }
  }, [
    gtSampleId,
    token,
    productRecognitionData,
    applyPairingsToPrData,
    applyVariantsToPrData,
    showErrorToast,
    showSuccessToast,
  ]);

  const handleSavePc = useCallback(async () => {
    if (!gtSampleId || !token || !pcInference) return;
    setIsSavingPc(true);
    try {
      await GtApiService.updateGtSamplePc(token, gtSampleId, {
        pc_inference: pcInference as unknown as import('../../services/gtApi').PcInference,
      });
      setIsPcDirty(false);
      showSuccessToast('PC inference saved');
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to save PC inference');
    } finally {
      setIsSavingPc(false);
    }
  }, [gtSampleId, token, pcInference, showErrorToast, showSuccessToast]);

  const hasPairingsNeedingVariants = currentPairings.size > 0;
  const variantsReady = allVariantsSelected(currentPairings);
  const savePrDisabled =
    isSavingPr || !productRecognitionData || (hasPairingsNeedingVariants && !variantsReady);
  const savePcDisabled = isSavingPc || !pcInference || !isPcDirty;

  const saveButton = (
    <div className="flex items-center gap-2">
      <button
        onClick={handleSavePr}
        disabled={savePrDisabled}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isSavingPr ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <SaveIcon fontSize="small" />
        )}
        {isSavingPr ? 'Saving...' : 'Save PR'}
      </button>
      <button
        onClick={handleSavePc}
        disabled={savePcDisabled}
        className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isSavingPc ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <SaveIcon fontSize="small" />
        )}
        {isSavingPc ? 'Saving...' : 'Save PC'}
      </button>
    </div>
  );

  return (
    <GtAudit
      productRecognitionData={productRecognitionData}
      planogramComplianceData={planogramComplianceData}
      pcInference={pcInference}
      onPogStatusApply={handlePogStatusApply}
      onRogStatusApply={handleRogStatusApply}
      onRemoveRogPosition={handleRemoveRogPosition}
      onAddRogPosition={handleAddRogPosition}
      onMoveRogPosition={handleMoveRogPosition}
      onTogglePrUuidForRogPosition={handleTogglePrUuidForRogPosition}
      isLoading={isLoading}
      imageId={imageId}
      onProductRecognitionDataChange={setProductRecognitionData}
      onPlanogramComplianceDataChange={setPlanogramComplianceData}
      actionButtons={saveButton}
      defaultPlanogramComplianceConfig={planogramComplianceConfig}
      pairingAddon={pairingAddon}
      onDetectionPogPairingsChange={handleDetectionPogPairingsChange}
      onStoreNameChange={handleStoreNameChange}
      renderTitleActions={renderTitleActions}
    />
  );
};

export default GroundTruthAudit;
